/**
 * PhotoGallery Component
 * Displays photos for a facility with voting, swipe navigation, and 360° panorama support
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../i18n/translations';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogContent,
  Menu,
  MenuItem,
  Skeleton
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import FlagIcon from '@mui/icons-material/Flag';
import View360Icon from '@mui/icons-material/Vrpano';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { photoService, FacilityPhoto } from '@services/photoService';
import { telegram } from '@services/telegram';

interface PhotoGalleryProps {
  facilityId: string;
  facilityType?: string;
  onUploadClick?: () => void;
  compact?: boolean;
}

export function PhotoGallery({ 
  facilityId, 
  facilityType = 'well',
  onUploadClick,
  compact = false 
}: PhotoGalleryProps) {
  const [photos, setPhotos] = useState<FacilityPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [voting, setVoting] = useState(false);
  const { t } = useTranslation();

  const currentUser = telegram.getUser();
  const currentUserId = currentUser?.id?.toString();

  // Load photos
  const loadPhotos = useCallback(async () => {
    setLoading(true);
    const fetchedPhotos = await photoService.getPhotos(facilityId, facilityType);
    setPhotos(fetchedPhotos);
    setLoading(false);
  }, [facilityId, facilityType]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  // Navigation
  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  // Voting
  const handleVote = async (photoId: string, voteType: 'up' | 'down') => {
    if (voting) return;
    setVoting(true);
    telegram.hapticFeedback('light');

    const photo = photos.find(p => p.id === photoId);
    if (!photo) {
      setVoting(false);
      return;
    }

    // Determine new vote type
    let newVoteType: 'up' | 'down' | 'none' = voteType;
    if (voteType === 'up' && photo.userUpvoted) newVoteType = 'none';
    if (voteType === 'down' && photo.userDownvoted) newVoteType = 'none';

    const result = await photoService.vote(photoId, newVoteType);
    
    if (result) {
      setPhotos(prev => prev.map(p => 
        p.id === photoId 
          ? {
              ...p,
              upvotes: result.upvotes,
              downvotes: result.downvotes,
              score: result.score,
              userUpvoted: result.userVote === 'up',
              userDownvoted: result.userVote === 'down'
            }
          : p
      ));
    }

    setVoting(false);
  };

  // Menu actions
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, photoId: string) => {
    setMenuAnchor(event.currentTarget);
    setSelectedPhotoId(photoId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedPhotoId(null);
  };

  const handleDelete = async () => {
    if (!selectedPhotoId) return;
    handleMenuClose();

    const confirmed = confirm(t('photoDeleteConfirm'));
    if (!confirmed) return;

    const success = await photoService.deletePhoto(selectedPhotoId);
    if (success) {
      setPhotos(prev => prev.filter(p => p.id !== selectedPhotoId));
      telegram.showAlert(t('photoDeleted'));
      
      // Adjust current index if needed
      if (currentIndex >= photos.length - 1 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    } else {
      telegram.showAlert(t('photoDeleteFailed'));
    }
  };

  const handleReport = async () => {
    if (!selectedPhotoId) return;
    handleMenuClose();

    telegram.showPopup({
      title: t('photoReportTitle'),
      message: t('photoReportMessage'),
      buttons: [
        { id: 'inappropriate', type: 'default', text: t('photoReportInappropriate') },
        { id: 'inaccurate', type: 'default', text: t('photoReportWrongLocation') },
        { id: 'spam', type: 'default', text: t('photoReportSpam') },
        { id: 'cancel', type: 'cancel', text: t('cancel') }
      ]
    }, async (buttonId: string) => {
      if (buttonId === 'cancel') return;
      
      const success = await photoService.report(
        selectedPhotoId!,
        buttonId as 'inappropriate' | 'inaccurate' | 'spam'
      );
      
      if (success) {
        telegram.showAlert(t('photoReportSubmitted'));
      } else {
        telegram.showAlert(t('photoReportFailed'));
      }
    });
  };

  // No photos state
  if (!loading && photos.length === 0) {
    return (
      <Box
        onClick={onUploadClick}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          borderRadius: 2,
          border: '2px dashed rgba(96, 165, 250, 0.3)',
          backgroundColor: 'rgba(15, 23, 42, 0.5)',
          cursor: onUploadClick ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          '&:hover': onUploadClick ? {
            borderColor: 'rgba(96, 165, 250, 0.6)',
            backgroundColor: 'rgba(96, 165, 250, 0.1)'
          } : {}
        }}
      >
        <PhotoCameraIcon sx={{ fontSize: 40, color: '#60a5fa', mb: 1 }} />
        <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center' }}>
          {t('photoNoPhotos')}
        </Typography>
        {onUploadClick && (
          <Typography variant="caption" sx={{ color: '#60a5fa', mt: 0.5 }}>
            {t('photoTapToAdd')}
          </Typography>
        )}
      </Box>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
        <Skeleton 
          variant="rectangular" 
          height={compact ? 120 : 200} 
          sx={{ bgcolor: 'rgba(96, 165, 250, 0.1)' }} 
        />
      </Box>
    );
  }

  const currentPhoto = photos[currentIndex];

  return (
    <>
      {/* Main Gallery View */}
      <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
        {/* Photo Display */}
        <Box
          onClick={() => setFullscreenOpen(true)}
          sx={{
            position: 'relative',
            height: compact ? 120 : 200,
            backgroundColor: 'rgba(15, 23, 42, 0.8)',
            cursor: 'pointer'
          }}
        >
          <img
            src={currentPhoto.thumbnailUrl}
            alt={currentPhoto.caption || t('photoFacilityPhoto')}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          
          {/* 360° Badge */}
          {currentPhoto.photo_type === 'panorama_360' && (
            <Chip
              icon={<View360Icon sx={{ fontSize: 16 }} />}
              label="360°"
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(139, 92, 246, 0.9)',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' }
              }}
            />
          )}
          
          {/* Photo Counter */}
          {photos.length > 1 && (
            <Chip
              label={`${currentIndex + 1}/${photos.length}`}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                fontSize: '0.7rem'
              }}
            />
          )}

          {/* Tags */}
          {currentPhoto.tags && currentPhoto.tags.length > 0 && !compact && (
            <Box sx={{ position: 'absolute', bottom: 8, left: 8, display: 'flex', gap: 0.5 }}>
              {currentPhoto.tags.slice(0, 2).map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    fontSize: '0.65rem',
                    height: 20
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Navigation Arrows */}
        {photos.length > 1 && (
          <>
            <IconButton
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              sx={{
                position: 'absolute',
                left: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                p: 0.5,
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              sx={{
                position: 'absolute',
                right: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                p: 0.5,
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </>
        )}

        {/* Voting Bar */}
        {!compact && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              backgroundColor: 'rgba(15, 23, 42, 0.95)',
              borderTop: '1px solid rgba(96, 165, 250, 0.2)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => handleVote(currentPhoto.id, 'up')}
                disabled={voting}
                sx={{ color: currentPhoto.userUpvoted ? '#22c55e' : '#94a3b8' }}
              >
                {currentPhoto.userUpvoted ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
              </IconButton>
              <Typography variant="body2" sx={{ 
                color: currentPhoto.score > 0 ? '#22c55e' : currentPhoto.score < 0 ? '#ef4444' : '#94a3b8',
                fontWeight: 600,
                minWidth: 24,
                textAlign: 'center'
              }}>
                {currentPhoto.score}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleVote(currentPhoto.id, 'down')}
                disabled={voting}
                sx={{ color: currentPhoto.userDownvoted ? '#ef4444' : '#94a3b8' }}
              >
                {currentPhoto.userDownvoted ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {currentPhoto.telegram_username && (
                <Typography variant="caption" sx={{ color: '#64748b', mr: 1 }}>
                  @{currentPhoto.telegram_username}
                </Typography>
              )}
              <IconButton
                size="small"
                onClick={(e) => handleMenuOpen(e, currentPhoto.id)}
                sx={{ color: '#94a3b8' }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: '#1e293b',
            border: '1px solid rgba(96, 165, 250, 0.2)',
            '& .MuiMenuItem-root': {
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(96, 165, 250, 0.1)' }
            }
          }
        }}
      >
        {selectedPhotoId && photos.find(p => p.id === selectedPhotoId)?.telegram_user_id === currentUserId && (
          <MenuItem onClick={handleDelete}>
            <DeleteIcon sx={{ mr: 1, color: '#ef4444' }} fontSize="small" />
            Delete
          </MenuItem>
        )}
        <MenuItem onClick={handleReport}>
          <FlagIcon sx={{ mr: 1, color: '#f59e0b' }} fontSize="small" />
          Report
        </MenuItem>
      </Menu>

      {/* Fullscreen Dialog */}
      <Dialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.98)',
            borderRadius: 2
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setFullscreenOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 10,
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
            }}
          >
            <CloseIcon />
          </IconButton>

          {currentPhoto.photo_type === 'panorama_360' ? (
            // 360° Panorama Viewer (Pannellum would be integrated here)
            <Box sx={{ 
              width: '100%', 
              height: '70vh', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: '#0f172a'
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <View360Icon sx={{ fontSize: 64, color: '#8b5cf6', mb: 2 }} />
                <Typography variant="h6" sx={{ color: 'white' }}>
                  360° Panorama
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  Drag to look around
                </Typography>
                {/* Pannellum iframe would go here */}
                <img
                  src={currentPhoto.fullUrl}
                  alt="360° panorama"
                  style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain', marginTop: 16 }}
                />
              </Box>
            </Box>
          ) : (
            <img
              src={currentPhoto.fullUrl}
              alt={currentPhoto.caption || 'Facility photo'}
              style={{
                width: '100%',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          )}

          {currentPhoto.caption && (
            <Box sx={{ p: 2, borderTop: '1px solid rgba(96, 165, 250, 0.2)' }}>
              <Typography variant="body2" sx={{ color: 'white' }}>
                {currentPhoto.caption}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
